import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { MapPin, Calendar, Plane, Building2, Compass, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PrintTrip() {
  const { id } = useParams();
  const [trip, setTrip] = useState(null);
  const [photos, setPhotos] = useState([]);   // [{id, blobUrl, caption}]
  const [photosReady, setPhotosReady] = useState(false);

  useEffect(() => {
    api.get(`/trips/${id}`).then(r => setTrip(r.data));
  }, [id]);

  // Fetch souvenir photos via authenticated blob → objectURL (works in Print)
  useEffect(() => {
    if (!trip) return;
    const svs = (trip.souvenirs || []).filter(s => s.type === "photo" && !s.is_deleted);
    if (svs.length === 0) { setPhotosReady(true); return; }
    (async () => {
      const token = localStorage.getItem("wl_token") || "";
      const out = [];
      for (const s of svs.slice(0, 24)) {
        try {
          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trips/${trip.trip_id}/souvenirs/${s.id}/download`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!r.ok) continue;
          const blob = await r.blob();
          out.push({ id: s.id, blobUrl: URL.createObjectURL(blob), caption: s.caption || "" });
        } catch {}
      }
      setPhotos(out);
      setPhotosReady(true);
    })();
    return () => { photos.forEach(p => URL.revokeObjectURL(p.blobUrl)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip]);

  useEffect(() => {
    if (trip && photosReady) setTimeout(() => window.print(), 500);
  }, [trip, photosReady]);

  if (!trip) return <div className="p-10 font-mono-code text-stone-500">Preparing your booklet…</div>;

  const totalSpent = (trip.expenses || []).reduce((s, e) => s + Number(e.amount || 0), 0);
  const totalPlanned = Object.values(trip.budget_planned || {}).reduce((s, v) => s + Number(v || 0), 0);

  return (
    <div className="min-h-screen bg-white text-stone-900 print:bg-white">
      <style>{`
        @media print {
          @page { size: A4; margin: 18mm 14mm; }
          .no-print { display: none !important; }
          .page-break { page-break-before: always; }
          .avoid-break { break-inside: avoid; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        @media screen {
          .print-container { max-width: 780px; margin: 0 auto; padding: 40px 24px; }
        }
      `}</style>
      <div className="print-container">
        <div className="no-print flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#C85A32] text-white flex items-center justify-center"><Compass size={16}/></div>
            <span className="font-serif-display text-lg font-bold">WanderLoom</span>
          </div>
          <Button onClick={()=>window.print()} className="bg-[#C85A32] hover:bg-[#B04B26]" data-testid="print-again-button"><Printer size={14} className="mr-1"/> Print / Save PDF</Button>
        </div>

        {/* Cover */}
        <div className="mb-10 avoid-break">
          <div className="font-mono-code text-[10px] uppercase tracking-[0.3em] text-amber-700 mb-2">WanderLoom · Trip Journal</div>
          <h1 className="font-serif-display text-5xl font-bold leading-[1.05] mb-3">{trip.title}</h1>
          <div className="flex flex-wrap gap-4 text-sm text-stone-600 border-t border-b border-stone-200 py-3 mt-4">
            <span className="flex items-center gap-1.5"><MapPin size={14}/> {trip.destination}</span>
            <span className="flex items-center gap-1.5"><Calendar size={14}/> {trip.days.length} days</span>
            {trip.start_date && <span className="font-mono-code text-xs">{trip.start_date} → {trip.end_date}</span>}
          </div>
          {trip.description && <p className="mt-4 text-stone-700 leading-relaxed">{trip.description}</p>}
        </div>

        {/* Days */}
        {trip.days.map(d => (
          <section key={d.day_number} className="mb-10 avoid-break">
            <div className="font-mono-code text-[10px] uppercase tracking-[0.25em] text-amber-700">Day {String(d.day_number).padStart(2,"0")}</div>
            <h2 className="font-serif-display text-3xl font-semibold mb-4">{d.theme || `Day ${d.day_number}`}</h2>
            <ol className="space-y-4">
              {d.activities.map((a, i) => (
                <li key={a.id} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-stone-900 text-white flex-none flex items-center justify-center text-xs font-mono-code">{i+1}</div>
                  <div className="flex-1 border-b border-dashed border-stone-200 pb-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {a.time && <span className="font-mono-code text-xs text-stone-500">{a.time}</span>}
                      <span className="font-mono-code text-[10px] uppercase px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800">{a.category}</span>
                      {a.cost > 0 && <span className="font-mono-code text-xs text-stone-500">${a.cost}</span>}
                    </div>
                    <div className="font-semibold mt-0.5 text-lg">{a.title}</div>
                    {a.location_name && <div className="text-xs text-stone-500 flex items-center gap-1"><MapPin size={11}/>{a.location_name}</div>}
                    {a.notes && <div className="text-sm text-stone-700 mt-1">{a.notes}</div>}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        ))}

        {trip.flights?.length > 0 && (
          <section className="mb-10 avoid-break page-break">
            <h2 className="font-serif-display text-3xl font-semibold flex items-center gap-2 mb-4"><Plane size={22}/> Flights</h2>
            <div className="grid grid-cols-2 gap-4">
              {trip.flights.map(f => (
                <div key={f.id} className="border border-stone-300 rounded-lg p-3">
                  <div className="font-mono-code text-xs text-sky-700">{f.airline} · {f.flight_no}</div>
                  <div className="text-lg font-semibold">{f.from_airport} → {f.to_airport}</div>
                  <div className="font-mono-code text-xs text-stone-500 mt-1">{f.departure_at} → {f.arrival_at}</div>
                  {f.confirmation && <div className="font-mono-code text-xs text-stone-500 mt-1">Conf: {f.confirmation}</div>}
                </div>
              ))}
            </div>
          </section>
        )}

        {trip.hotels?.length > 0 && (
          <section className="mb-10 avoid-break">
            <h2 className="font-serif-display text-3xl font-semibold flex items-center gap-2 mb-4"><Building2 size={22}/> Stays</h2>
            <div className="grid grid-cols-2 gap-4">
              {trip.hotels.map(h => (
                <div key={h.id} className="border border-stone-300 rounded-lg p-3">
                  <div className="font-semibold">{h.name}</div>
                  {h.address && <div className="text-xs text-stone-500">{h.address}</div>}
                  <div className="font-mono-code text-xs text-stone-500 mt-1">{h.check_in} → {h.check_out}</div>
                  {h.confirmation && <div className="font-mono-code text-xs text-stone-500">Conf: {h.confirmation}</div>}
                </div>
              ))}
            </div>
          </section>
        )}

        {(trip.expenses?.length > 0 || totalPlanned > 0) && (
          <section className="mb-10 avoid-break">
            <h2 className="font-serif-display text-3xl font-semibold mb-4">Budget</h2>
            <div className="grid grid-cols-3 gap-3 text-center border border-stone-200 rounded-lg p-3">
              <div><div className="font-mono-code text-[10px] uppercase text-stone-500">Planned</div><div className="font-serif-display text-2xl font-bold">${totalPlanned}</div></div>
              <div><div className="font-mono-code text-[10px] uppercase text-stone-500">Spent</div><div className="font-serif-display text-2xl font-bold">${totalSpent}</div></div>
              <div><div className="font-mono-code text-[10px] uppercase text-stone-500">Remaining</div><div className="font-serif-display text-2xl font-bold">${totalPlanned - totalSpent}</div></div>
            </div>
          </section>
        )}

        {trip.packing?.length > 0 && (
          <section className="mb-10 avoid-break">
            <h2 className="font-serif-display text-3xl font-semibold mb-4">Packing list</h2>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              {trip.packing.map(p => (
                <li key={p.id} className="flex items-center gap-2">
                  <span className={`w-4 h-4 border ${p.checked?"bg-stone-900 border-stone-900 text-white text-[10px] flex items-center justify-center":"border-stone-400"}`}>{p.checked?"✓":""}</span>
                  <span className={p.checked?"line-through text-stone-400":""}>{p.item}</span>
                  <span className="font-mono-code text-[10px] text-stone-400 uppercase">{p.category}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {photos.length > 0 && (
          <section className="mb-10 page-break">
            <div className="font-mono-code text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-2">Chapter · Memories</div>
            <h2 className="font-serif-display text-4xl font-semibold mb-8">The trip, in pictures.</h2>
            <div className="grid grid-cols-6 gap-3">
              {photos.map((p, i) => {
                // Magazine-ish layout: alternate large/small cells
                const large = (i % 5 === 0);
                return (
                  <figure key={p.id} className={`${large ? "col-span-6 md:col-span-4 row-span-2" : "col-span-3 md:col-span-2"} avoid-break`}>
                    <div className={`overflow-hidden bg-stone-100 ${large ? "aspect-[3/2]" : "aspect-square"}`}>
                      <img src={p.blobUrl} alt="" className="w-full h-full object-cover" />
                    </div>
                    {p.caption && (
                      <figcaption className="mt-1.5 font-serif-display italic text-sm text-stone-700 leading-snug">{p.caption}</figcaption>
                    )}
                  </figure>
                );
              })}
            </div>
          </section>
        )}

        <footer className="mt-16 pt-6 border-t border-stone-200 text-center font-mono-code text-[10px] uppercase tracking-[0.3em] text-stone-400">
          Made with WanderLoom · Bon voyage
        </footer>
      </div>
    </div>
  );
}
